import { Component, OnInit, AfterViewInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MatSort, PageEvent } from '@angular/material';
import { BreadcrumbsService } from 'src/app/services/utils/breadcrumbs.service';
import { PortOutService, PortOut, PortOutFilter } from 'src/app/services/api/port-out/port-out.service';
import { Title } from '@angular/platform-browser';
import { OrderStatusObject } from 'src/app/models/orders/order-status-object.model';
import * as moment from 'moment';

@Component({
  selector: 'app-port-out',
  templateUrl: './port-out.component.html',
  styleUrls: ['./port-out.component.scss']
})
export class PortOutComponent implements OnInit, AfterViewInit {

  @ViewChild(MatSort, { static: false }) sort: MatSort;

  elements: PortOut[] = [];
  totalElements = 0;
  pageSize = 20;
  pageIndex = 0;
  sortField = 'createdDate';
  sortDirection = 'desc';

  displayedColumns: string[] = [
    'msisdn',
    'billingAccount',
    'initPortOutSmsSent',
    'remoteIdo',
    'newStatus',
    'windowTime',
    'createdDate',
    'ponr',
    'portoutFinalized',
    'portOutTaskId',
    'arsTerminationSentResult',
    'actions'
  ];

  form: FormGroup;

  tooltip = {
    refresh: '',
    detail: ''
  };

  statusOptions = [
    'CONFIGURED', 'CHECKING', 'CONFIGURING',
    'REQUESTED',
    'ANSWERED',
    'PROCESSING',
    'PROCESSED',
    'REGULARIZED',
    'FAILED',
    'CANCELLED',
    'OTROS'
  ];

  constructor(
    private portOutService: PortOutService,
    private router: Router,
    private fb: FormBuilder,
    private translate: TranslateService,
    private breadcrumbsService: BreadcrumbsService,
    private titleService: Title
  ) {}

  ngOnInit() {
    if (this.titleService.getTitle().includes(' - ')) {
      this.titleService.setTitle(this.titleService.getTitle().split(' - ')[1]);
    }
    this.breadcrumbsService.sendBreadcrumbs({
      breadcrumbs: [
        { title: 'Administración', url: 'provision/management' },
        { title: 'Portabilidades salientes', url: 'provision/management/port-out' }
      ]
    });

    this.form = this.fb.group({
      msisdn: [''],
      billingAccount: [''],
      initPortOutSmsSent: [null],
      remoteIdo: [''],
      newStatus: [''],
      windowTimeFrom: [null],
      windowTimeTo: [null],
      createdDateFrom: [null],
      createdDateTo: [null],
      ponr: [null],
      portoutFinalized: [null],
      portOutTaskId: [''],
      arsTerminationSentResult: [null]
    });

    this.translate.get('DUMMY.key').subscribe(() => {
      this.tooltip.refresh = this.translate.instant('TOOLTIPS.refreshResults');
      this.tooltip.detail = this.translate.instant('PORT_OUT_KEY.detail');
    });

    this.search();
  }

  ngAfterViewInit() {
    this.sort.sortChange.subscribe(s => {
      this.sortField = s.active;
      this.sortDirection = s.direction || 'desc';
      this.search();
    });
  }

  search(resetPage = true) {
    if (resetPage) { this.pageIndex = 0; }
    const f = this.form.value;
    const filter: PortOutFilter = {
      msisdn: f.msisdn || undefined,
      billingAccount: f.billingAccount || undefined,
      initPortOutSmsSent: f.initPortOutSmsSent != null ? f.initPortOutSmsSent : undefined,
      remoteIdo: f.remoteIdo || undefined,
      newStatus: f.newStatus || undefined,
      windowTimeFrom: f.windowTimeFrom ? moment(f.windowTimeFrom).format('YYYY-MM-DD') : undefined,
      windowTimeTo: f.windowTimeTo ? moment(f.windowTimeTo).format('YYYY-MM-DD') : undefined,
      createdDateFrom: f.createdDateFrom ? moment(f.createdDateFrom).format('YYYY-MM-DD') : undefined,
      createdDateTo: f.createdDateTo ? moment(f.createdDateTo).format('YYYY-MM-DD') : undefined,
      ponr: f.ponr != null && f.ponr !== '' ? f.ponr : undefined,
      portoutFinalized: f.portoutFinalized != null && f.portoutFinalized !== '' ? f.portoutFinalized : undefined,
      portOutTaskId: f.portOutTaskId || undefined,
      arsTerminationSentResult: f.arsTerminationSentResult || undefined,
      page: this.pageIndex,
      size: this.pageSize,
      sortField: this.sortField,
      sortDirection: this.sortDirection
    };

    this.portOutService.search(filter).subscribe(data => {
      this.elements = data.content;
      this.totalElements = data.totalElements;
    });
  }

  clearFilters() {
    this.form.reset();
    this.search();
  }

  goToDetail(portOut: PortOut) {
    this.router.navigate(['/provision/management/port-out/detail'], {
      queryParams: { id: portOut.id }
    });
  }

  onPageChange(event: PageEvent) {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.search(false);
  }

  getPortOutStatus(status: string): OrderStatusObject {
    if (!status) { return new OrderStatusObject('', 'help', ''); }
    switch (status.toUpperCase()) {
      // Estados internos previos a la solicitud al NC
      case 'CONFIGURED':
      case 'CHECKING':
      case 'CONFIGURING':  return new OrderStatusObject(status, 'settings', 'pending');
      // Solicitada al NC (ASOL)
      case 'REQUESTED':    return new OrderStatusObject(status, 'send', 'inprogress');
      // Aprobada por el NC (ACON)
      case 'ANSWERED':     return new OrderStatusObject(status, 'thumb_up', 'inprogress');
      // Pasado el PONR, en ventana de cambio (ACON/APOR)
      case 'PROCESSING':   return new OrderStatusObject(status, 'access_time', 'inprogress');
      // Completada (APOR)
      case 'PROCESSED':    return new OrderStatusObject(status, 'done_all', 'completed');
      // Corrección interna BSS
      case 'REGULARIZED':  return new OrderStatusObject(status, 'autorenew', 'completed');
      // Rechazada o error (AREC / error interno)
      case 'FAILED':       return new OrderStatusObject(status, 'close', 'failed');
      // Cancelada (ACAN o previa al NC)
      case 'CANCELLED':    return new OrderStatusObject(status, 'cancel', 'cancelled');
      default:             return new OrderStatusObject(status, 'help', 'unknown');
    }
  }
}
